/** @jsx isolateComponent */
import { useRef } from 'react';
import { uniqueId } from 'lodash';

import { InterpolationWithTheme } from '@emotion/core';
import { isolateComponent, withModifier } from '@zapier/style-encapsulation';
import { Animation, Colors, Tooltip } from '@zapier/design-system';

export const DOT_SIZE = '30px';

export const ACTIVE = 'ACTIVE';
export const ENABLED = 'ENABLED';
export const DISABLED = 'DISABLED';

export type DotState = typeof ACTIVE | typeof ENABLED | typeof DISABLED;
export const states: DotState[] = [ACTIVE, ENABLED, DISABLED];

type NavDotProps = {
  /** Number that is displayed in the dot. */
  number: number;
  /** Callback function that is fired on click. */
  onClick: () => void;
  /** Called when the dot receives `focus` or `mouseenter`. */
  onFocus: () => void;
  /** Called when the button receives `blur` or `mouseout`. */
  onBlur: () => void;
  /** Which state this dot is in. */
  state: DotState;
  /** A tooltip that conditionally displays. */
  tooltip: string;
  /** True if this dots tooltip should be visible */
  showTooltip: boolean;
};

const Styles = {
  root: (state: DotState) => [
    {
      height: DOT_SIZE,
      width: DOT_SIZE,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: '16px',
      position: 'relative',

      // This is the outline that renders around the button when
      // it's focused. It's a pseudo element to preserve transparency
      // between the edge of the button and the edge of the outline.
      // It's defined here vs only in `:focus` so that it can transition
      // between states.
      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        border: '2px solid transparent',
        borderRadius: '36px',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        transitionProperty: 'all',
        transitionTimingFunction: Animation.transitionTimingFunction,
        transitionDuration: Animation.transitionDuration,
      },
    },
    withModifier(
      state,
      {
        [ENABLED]: {
          cursor: 'pointer',
          background: Colors.neutral200,
          color: Colors.neutral700,
          '&:hover': {
            background: Colors.blue10,
          },
          '&:focus': {
            '&::before': {
              borderColor: Colors.blueJeans,
              top: -3,
              right: -3,
              bottom: -3,
              left: -3,
            },
          },
        },
        [ACTIVE]: {
          background: Colors.neutral700,
          color: Colors.neutral100,
          '&:hover': {
            background: Colors.blueJeans,
          },
        },
        [DISABLED]: {
          background: Colors.neutral200,
          color: Colors.neutral600,
          '&:focus': {
            '&::before': {
              borderColor: Colors.blueJeans,
              top: -3,
              right: -3,
              bottom: -3,
              left: -3,
            },
          },
        },
      }[state]
    ),
  ],
};

/**
 * Renders a clickable nav dot.
 */
const NavDot = ({
  number,
  onClick,
  state,
  tooltip,
  showTooltip = false,
  onFocus,
  onBlur,
}: NavDotProps) => {
  // useRef() memoizes the value of uniqueId() for the lifetime of the component.
  const { current: id } = useRef(uniqueId());

  const enabledOnlyProps =
    state === ENABLED
      ? {
          onClick,
          onFocus,
          onMouseEnter: onFocus,
        }
      : {};

  return (
    <button
      aria-describedby={id}
      css={Styles.root(state) as InterpolationWithTheme<any>}
      disabled={state !== ENABLED}
      onBlur={onBlur}
      onMouseOut={onBlur}
      type="button"
      {...enabledOnlyProps}
    >
      {number}
      {showTooltip && (
        <Tooltip id={id} position="north">
          {tooltip}
        </Tooltip>
      )}
    </button>
  );
};

export default NavDot;
